export type ThemeObject = Record<string, any>;

export type ColorPairMerge = 'mergeKeys' | 'replaceWhenSet';

export type BrandColorStrategy = 'replacesGlobalThemeColors' | 'fillsUnsetColors';

export interface DeepMergeOptions {
  // `mergeKeys` (default): an override of `{ light }` alone keeps the existing `dark`.
  // `replaceWhenSet`: the override pair replaces the whole pair once any value is non-empty.
  colorPairs?: ColorPairMerge;
}

export interface MergeThemeOptions {
  // `replacesGlobalThemeColors`: a global theme's colours become brandColor, before the overrides.
  // `fillsUnsetColors`: after the overrides, unset colours fall back to brandColor, then black.
  brandColor: BrandColorStrategy;
}

export interface MergeThemeTheme {
  scope?: string | null;
  variables?: ThemeObject | null;
}

export interface MergeThemeOverrides {
  variables?: ThemeObject | null;
}

export interface MergeThemeSeller {
  brandColor?: string | null;
}

const isObject = (value: unknown): value is ThemeObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSetColorPair = (value: ThemeObject) =>
  (value.light !== undefined || value.dark !== undefined) &&
  Object.keys(value).some(
    key => value[key] !== null && value[key] !== undefined && value[key] !== '',
  );

// A null or undefined in the source never clears a value, and with no source `target` itself is
// returned (not a copy), so callers must not mutate the result.
export function deepMergeWithBooleans(
  target: ThemeObject,
  source: ThemeObject | null | undefined,
  options: DeepMergeOptions = {},
): ThemeObject {
  if (!source) return target;

  const output: ThemeObject = { ...target };

  Object.keys(source).forEach(key => {
    const value = source[key];
    if (value === null || value === undefined) return;

    if (isObject(value) && isObject(target[key])) {
      output[key] =
        options.colorPairs === 'replaceWhenSet' && isSetColorPair(value)
          ? value
          : deepMergeWithBooleans(target[key], value, options);
      return;
    }

    output[key] = value;
  });

  return output;
}

// Fresh per call: the merge may return it as is, and the API mutates the result's labels.
const defaultThemeVariables = (): ThemeObject => ({
  layout: {
    splitLayout: { mainColumn: 'left' },
    doubleLayout: { formPosition: 'right' },
  },
  tokens: {},
  elements: {
    product: {
      logo: {
        hostedPageLogoStyle: 'logoBanner',
        logoBubble: { size: 80, styles: { textAlign: 'center' } },
      },
      pricePicker: { groupBy: 'auto' },
    },
  },
  labels: {},
});

const colorPair = (color: string) => ({ light: color, dark: color });

function fillUnsetColors(theme: ThemeObject, brandColor: string | null | undefined): ThemeObject {
  let result = theme;

  if (!result.tokens.colorPrimary) {
    result = deepMergeWithBooleans(result, {
      tokens: { colorPrimary: colorPair(brandColor || '#000000') },
    });
  }

  if (!result.tokens.colorSecondary) {
    const primary = result.tokens.colorPrimary;
    result = deepMergeWithBooleans(result, {
      tokens: {
        colorSecondary: {
          light: primary?.light || brandColor || '#000000',
          dark: primary?.dark || brandColor || '#000000',
        },
      },
    });
  }

  return result;
}

export function mergeTheme(
  theme: MergeThemeTheme | null | undefined,
  themeOverrides: MergeThemeOverrides | null | undefined,
  seller: MergeThemeSeller | null | undefined,
  { brandColor }: MergeThemeOptions,
): ThemeObject {
  let result = deepMergeWithBooleans(defaultThemeVariables(), theme?.variables);

  if (
    brandColor === 'replacesGlobalThemeColors' &&
    theme?.scope === 'global' &&
    seller?.brandColor
  ) {
    result = deepMergeWithBooleans(result, {
      tokens: {
        colorPrimary: colorPair(seller.brandColor),
        colorSecondary: colorPair(seller.brandColor),
      },
    });
  }

  result = deepMergeWithBooleans(result, themeOverrides?.variables);

  if (brandColor === 'fillsUnsetColors') {
    result = fillUnsetColors(result, seller?.brandColor);
  }

  return result;
}
